import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShipmentStatusRequest } from '../../api/generated/models/ShipmentStatusRequest';
import { usePortalDropdown } from '../../hooks/usePortalDropdown';
import PortalDropdown from '../ui/PortalDropdown';
import MoreIcon from '../Icons/MoreIcon';

const STATUSES = [
  ShipmentStatusRequest.executionStatus.PROGRESS,
  ShipmentStatusRequest.executionStatus.READY_FOR_DEPARTURE,
  ShipmentStatusRequest.executionStatus.IN_TRANSIT,
  ShipmentStatusRequest.executionStatus.ARRIVED,
  ShipmentStatusRequest.executionStatus.CLEARANCE_TRANSPORT,
  ShipmentStatusRequest.executionStatus.DELIVERED,
] as const;

interface ShipmentCardMenuProps {
  currentStatus: string | null | undefined;
  onChangeStatus: (status: ShipmentStatusRequest.executionStatus) => void;
  onArchive: () => void;
}

const ShipmentCardMenu: React.FC<ShipmentCardMenuProps> = ({
  currentStatus,
  onChangeStatus,
  onArchive,
}) => {
  const { t } = useTranslation();
  const { open, coords, triggerRef, dropdownRef, toggle, close } = usePortalDropdown();
  // Drill-in navigation: 'main' shows the actions, 'status' shows the status
  // list behind a back button. Keeps everything in one fixed-width panel so it
  // never needs a flyout that would clip on narrow screens.
  const [view, setView] = useState<'main' | 'status'>('main');

  // Always reopen on the main view.
  useEffect(() => {
    if (!open) setView('main');
  }, [open]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggle();
  };

  const handleStatusPick = (e: React.MouseEvent, status: ShipmentStatusRequest.executionStatus) => {
    e.stopPropagation();
    onChangeStatus(status);
    close();
  };

  const handleArchive = (e: React.MouseEvent) => {
    e.stopPropagation();
    onArchive();
    close();
  };

  return (
    <>
      <button
        ref={triggerRef}
        className="portal-dropdown__trigger"
        onClick={handleToggle}
        aria-label="Menu"
      >
        <MoreIcon size={18} horizontal />
      </button>

      <PortalDropdown open={open} coords={coords} dropdownRef={dropdownRef}>
        {view === 'main' ? (
          <>
            <button
              className="portal-dropdown__item"
              onClick={(e) => { e.stopPropagation(); setView('status'); }}
            >
              {t('board_menu_change_status')}
              <span className="shipment-card-menu__chevron">›</span>
            </button>

            <button className="portal-dropdown__item shipment-card-menu__item--danger" onClick={handleArchive}>
              {t('board_menu_archive')}
            </button>
          </>
        ) : (
          <>
            <button
              className="shipment-card-menu__back"
              onClick={(e) => { e.stopPropagation(); setView('main'); }}
            >
              <span className="shipment-card-menu__back-arrow">‹</span>
              {t('back')}
            </button>

            {STATUSES.map((s) => (
              <button
                key={s}
                className="portal-dropdown__item"
                onClick={(e) => handleStatusPick(e, s)}
              >
                {t(`shipment_status_${s}`)}
                <span className={`shipment-card-menu__radio${currentStatus === s ? ' shipment-card-menu__radio--checked' : ''}`} />
              </button>
            ))}
          </>
        )}
      </PortalDropdown>
    </>
  );
};

export default ShipmentCardMenu;
